import React from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import UserJourneyManager, { UserJourneyStates, UserJourneyActions } from '../../../utility/userJourneyManager';

const UserJourneyModal = ({ 
    show, 
    onHide, 
    userState, 
    readinessAssessments = [], 
    progressAssessments = [],
    languageUse = 'English',
    onStartReadiness,
    onStartProgress
}) => {
    const navigate = useNavigate();
    const journeyManager = new UserJourneyManager();
    
    const context = journeyManager.getUserJourneyContext(userState, languageUse);
    const actions = journeyManager.getRecommendedActions(userState, readinessAssessments, progressAssessments);

    const handleAction = (action) => {
        switch (action.action) {
            case UserJourneyActions.START_READINESS:
                if (onStartReadiness) {
                    onStartReadiness();
                } else {
                    // Default behavior - navigate to history and trigger start
                    navigate('/user/history?action=start_readiness');
                }
                onHide();
                break;

            case UserJourneyActions.RESUME_READINESS:
                if (action.assessmentId) {
                    navigate(`/user/assessment/${action.assessmentId}`);
                }
                onHide();
                break;

            case UserJourneyActions.START_PROGRESS:
                if (onStartProgress) {
                    onStartProgress();
                } else {
                    // Default behavior - navigate to history and trigger start
                    navigate('/user/history?action=start_progress');
                }
                onHide();
                break;

            case UserJourneyActions.RESUME_PROGRESS:
                if (action.assessmentId) {
                    navigate(`/user/progress/assessment/${action.assessmentId}`);
                }
                onHide();
                break;

            case UserJourneyActions.VIEW_DASHBOARD:
                if (action.assessmentId) {
                    const encryptedId = CryptoJS.AES.encrypt(action.assessmentId, 'mySecretKey').toString();
                    if (userState === UserJourneyStates.PROGRESS_ACTIVE || 
                        userState === UserJourneyStates.PROGRESS_COMPLETED || 
                        userState === UserJourneyStates.EXPERIENCED) {
                        navigate(`/user/progress/dashboard/${encodeURIComponent(encryptedId)}`);
                    } else {
                        navigate(`/user/dashboard/${encodeURIComponent(encryptedId)}`);
                    }
                }
                onHide();
                break;

            case UserJourneyActions.VIEW_REPORTS:
                navigate('/user/history?action=view_reports');
                onHide();
                break;

            default:
                onHide();
                break;
        }
    };

    const getModalSize = () => {
        switch (userState) {
            case UserJourneyStates.FIRST_TIME:
            case UserJourneyStates.RETURNING_INCOMPLETE:
                return 'lg';
            case UserJourneyStates.READINESS_COMPLETED:
            case UserJourneyStates.PROGRESS_ACTIVE:
            case UserJourneyStates.PROGRESS_COMPLETED:
            case UserJourneyStates.EXPERIENCED:
                return 'md';
            default:
                return 'lg';
        }
    };

    return (
        <Modal show={show} onHide={onHide} size={getModalSize()} centered>
            <Modal.Header closeButton className={`bg-${context.color} text-white`}>
                <Modal.Title>
                    <i className={`bi ${context.icon} me-2`}></i>
                    {context.title}
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-4">
                <div className="text-center mb-4">
                    <h5 className="text-muted mb-3">{context.subtitle}</h5>
                    <p className="text-muted">{context.description}</p>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    {actions.map((action, index) => (
                        <div key={index} className="mb-3">
                            <Button
                                variant={action.buttonVariant}
                                size="lg"
                                className="w-100 d-flex align-items-center justify-content-center"
                                onClick={() => handleAction(action)}
                            >
                                <i className={`bi ${action.icon} me-2`}></i>
                                {action.buttonText}
                            </Button>
                            {action.description && (
                                <small className="text-muted d-block mt-1 text-center">
                                    {action.description}
                                </small>
                            )}
                        </div>
                    ))}
                </div>

                {/* Additional Context for First-time Users */}
                {userState === UserJourneyStates.FIRST_TIME && (
                    <div className="mt-4 p-3 bg-light rounded">
                        <h6 className="mb-3">
                            <i className="bi bi-info-circle me-2"></i>
                            {languageUse === 'English' ? 'What to Expect' : 'À quoi s\'attendre'}
                        </h6>
                        <Row>
                            <Col md={4} className="text-center mb-3">
                                <div className="feature-item">
                                    <i className="bi bi-clock text-primary" style={{ fontSize: '2rem' }}></i>
                                    <h6 className="mt-2">{languageUse === 'English' ? '30-45 Minutes' : '30-45 minutes'}</h6>
                                    <small className="text-muted">
                                        {languageUse === 'English' ? 'Estimated completion time' : 'Temps de completion estimé'}
                                    </small>
                                </div>
                            </Col>
                            <Col md={4} className="text-center mb-3">
                                <div className="feature-item">
                                    <i className="bi bi-shield-check text-success" style={{ fontSize: '2rem' }}></i>
                                    <h6 className="mt-2">{languageUse === 'English' ? 'Auto-Save' : 'Sauvegarde automatique'}</h6>
                                    <small className="text-muted">
                                        {languageUse === 'English' ? 'Your progress is saved automatically' : 'Votre progression est sauvegardée automatiquement'}
                                    </small>
                                </div>
                            </Col>
                            <Col md={4} className="text-center mb-3">
                                <div className="feature-item">
                                    <i className="bi bi-file-earmark-text text-info" style={{ fontSize: '2rem' }}></i>
                                    <h6 className="mt-2">{languageUse === 'English' ? 'Detailed Report' : 'Rapport détaillé'}</h6>
                                    <small className="text-muted">
                                        {languageUse === 'English' ? 'Get comprehensive insights' : 'Obtenez des insights complets'}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Progress Recovery for Returning Users */}
                {userState === UserJourneyStates.RETURNING_INCOMPLETE && (
                    <div className="mt-4 p-3 bg-warning bg-opacity-10 rounded">
                        <h6 className="mb-2">
                            <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                            {languageUse === 'English' ? 'Progress Recovery' : 'Récupération de progression'}
                        </h6>
                        <p className="mb-0 small">
                            {languageUse === 'English' 
                                ? 'We found an incomplete assessment. You can resume from where you left off or start fresh.'
                                : 'Nous avons trouvé une évaluation incomplète. Vous pouvez reprendre où vous vous êtes arrêté ou recommencer.'}
                        </p>
                    </div>
                )}

                {/* Next Steps for Completed Readiness */}
                {userState === UserJourneyStates.READINESS_COMPLETED && (
                    <div className="mt-4 p-3 bg-success bg-opacity-10 rounded">
                        <h6 className="mb-2">
                            <i className="bi bi-check-circle me-2 text-success"></i>
                            {languageUse === 'English' ? 'Ready for Progress Tracking' : 'Prêt pour le suivi des progrès'}
                        </h6>
                        <p className="mb-0 small">
                            {languageUse === 'English'
                                ? 'Your baseline is established! Now track your ESG improvements over time.'
                                : 'Votre base de référence est établie ! Suivez maintenant vos améliorations ESG au fil du temps.'}
                        </p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                    {languageUse === 'English' ? 'Maybe Later' : 'Plus tard'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserJourneyModal; 